// @flow

import { find, get, isNumber } from 'lodash';

import features from '../features';
import formatPlanTiers from '../formatPlanTiers';
import isTierFree from '../isTierFree';

import type {
  AccountPlan,
  Features,
  Interval,
  Plan,
} from '@zapier/billing/src/types';

const createShortName = (name: string = ''): string =>
  name.replace('Professional', 'Pro').replace('Plus', '+');

const findFreeMeteredTier = (tiers = []) =>
  find(tiers, isTierFree) || {
    numTasks: 0,
    pricePerTask: 0,
  };

const getFeatureProperties = (plan: AccountPlan): Features =>
  ((features.reduce(
    (acc, feature) => ({
      ...acc,
      [feature.key]: feature.hasFeature(plan),
    }),
    {}
  ): any): Features);

const getNumFreeMeteredTasks = (plan: AccountPlan) =>
  findFreeMeteredTier(formatPlanTiers(plan)).numTasks;

export default (
  accountPlan: AccountPlan,
  selectedInterval: ?Interval | string
): Plan => {
  const intervals = get(accountPlan, 'intervals', []);
  const interval =
    find(intervals, { interval: selectedInterval }) || intervals[0];

  const monthlyAmount = get(interval, 'monthlyAmount');
  const hasMonthlyAmount = isNumber(monthlyAmount);
  const amount = hasMonthlyAmount ? monthlyAmount : get(interval, 'amount');
  const billingAmount = get(interval, 'amount');
  const billingInterval = get(interval, 'interval');
  const tiers = formatPlanTiers(accountPlan);
  const metadata = get(accountPlan, 'metadata');
  const metaFeatures = get(metadata, 'features');
  const description = get(metadata, 'description');
  const metaName = get(metadata, 'name');
  const featureProps = getFeatureProperties(accountPlan);
  const taskLimitVariants = get(accountPlan, 'task_limit_variants', []);
  const name = get(accountPlan, 'name');
  const descriptiveName = get(accountPlan, 'descriptive_name');
  const displayName = get(accountPlan, 'short_name');
  const type = get(accountPlan, 'plan_type');
  const allowCompanies = get(accountPlan, 'allow_companies', false);
  const tasks = featureProps.hasMeteredBilling
    ? getNumFreeMeteredTasks(accountPlan)
    : get(accountPlan, 'actions');

  return {
    ...featureProps,
    actions: tasks,
    amount,
    allowCompanies,
    billingAmount,
    billingInterval,
    description,
    descriptiveName,
    displayName,
    features: metaFeatures,
    id: get(interval, 'id'),
    intervals,
    isInPlanGroup: !get(accountPlan, 'off_plan_group', false),
    name: name || metaName,
    selectedTaskLimitVariant: taskLimitVariants[0],
    shortName: createShortName(metaName || name),
    taskLimitVariants,
    tasks,
    tiers,
    type,
    zaps: get(accountPlan, 'zaps'),
    zapsRunInterval: get(accountPlan, 'polling_interval_minutes'),
  };
};
